
import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import type { User } from "@/lib/types/database"
import { authApi } from "@/lib/api/auth"
import { usersApi, type ApiUserWithTeams } from "@/lib/api/users"
import { api } from "@/lib/api/client"
import { mapApiUserToUser } from "@/lib/api/mappers"
import {
  clearActiveOrg,
  clearOrgSnapshot,
  restoreActiveOrg,
  snapshotActiveOrg,
} from "@/lib/organization/storage"

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isImpersonating: boolean
  hasTeams: boolean
  signIn: (email: string, password: string) => Promise<{ hasTeams: boolean }>
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<{ hasTeams: boolean }>
  signOut: () => Promise<void>
  impersonateAs: (token: string) => Promise<void>
  endImpersonation: () => void
  /** Re-fetches /auth/profile, updates context state, and returns the fresh
   *  ApiUserWithTeams so callers (e.g. invite.tsx) can read the just-joined
   *  team without waiting for React state to propagate. */
  refreshUser: () => Promise<ApiUserWithTeams>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasTeams, setHasTeams] = useState(false)
  const [isImpersonating, setIsImpersonating] = useState(
    () => sessionStorage.getItem("etx_impersonating") === "true",
  )

  const loadUser = async (): Promise<ApiUserWithTeams> => {
    const apiUser = await usersApi.getMe()
    setUser(mapApiUserToUser(apiUser))
    setHasTeams(apiUser.teams.length > 0)
    return apiUser
  }

  useEffect(() => {
    const token = localStorage.getItem("etx_access_token")
    if (token) {
      loadUser()
        .catch(() => { api.clearToken() })
        .finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    const { accessToken } = await authApi.signIn({ email, password })
    api.setToken(accessToken)
    const apiUser = await loadUser()
    return { hasTeams: apiUser.teams.length > 0 }
  }

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    const signupKey = localStorage.getItem("etx_signup_key") ?? undefined
    const { accessToken } = await authApi.signUp({ email, password, firstName, lastName }, signupKey)
    api.setToken(accessToken)
    const apiUser = await loadUser()
    return { hasTeams: apiUser.teams.length > 0 }
  }

  const signOut = async () => {
    setUser(null)
    setHasTeams(false)
    api.clearToken()
    sessionStorage.removeItem("etx_impersonating")
    sessionStorage.removeItem("etx_original_token")
    // Drop the impersonated user's active-team selection AND any lingering
    // admin snapshot — next login must start from a clean slate, otherwise
    // OrganizationContext re-hydrates a team the new user isn't a member of
    // and every team-scoped query 401s.
    clearActiveOrg()
    clearOrgSnapshot()
    setIsImpersonating(false)
  }

  const impersonateAs = async (token: string) => {
    const currentToken = localStorage.getItem("etx_access_token")
    if (currentToken) {
      sessionStorage.setItem("etx_original_token", currentToken)
    }
    // Stash admin's active-org keys to sessionStorage, then clear them so the
    // impersonated user's OrganizationContext init effect picks fresh defaults
    // rather than inheriting the admin's selection.
    snapshotActiveOrg()
    clearActiveOrg()
    api.setToken(token)
    sessionStorage.setItem("etx_impersonating", "true")
    setIsImpersonating(true)
    await loadUser()
  }

  const endImpersonation = () => {
    const originalToken = sessionStorage.getItem("etx_original_token")
    // Restore admin's active-org keys BEFORE loadUser() fires — the org init
    // effect re-reads localStorage when teams data lands, so the snapshot must
    // be back in place by then. Also clears the impersonated user's leftover
    // keys if no snapshot exists.
    if (originalToken) {
      restoreActiveOrg()
      api.setToken(originalToken)
    } else {
      // No way back to the admin session — fall through to a clean logout.
      api.clearToken()
      clearActiveOrg()
      clearOrgSnapshot()
    }
    sessionStorage.removeItem("etx_impersonating")
    sessionStorage.removeItem("etx_original_token")
    setIsImpersonating(false)
    setUser(null)
    setHasTeams(false)
    if (originalToken) {
      loadUser().catch(() => { api.clearToken() })
    }
  }

  const refreshUser = async () => {
    return loadUser()
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, isImpersonating, hasTeams, signIn, signUp, signOut, impersonateAs, endImpersonation, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
